import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

function DashboardNacional({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-nacional/estatisticas`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Nacional</h1>

      {/* Cards Superiores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Card Renda Fixa */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Renda Fixa</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_renda_fixa)}</p>
              <p className="text-xs text-gray-500 mt-1">Poupança + Tesouro</p>
            </div>
          </div>
        </div>

        {/* Card Empresas */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Empresas</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_empresas)}</p>
              <p className="text-xs text-gray-500 mt-1">Bluechips + Smallcaps</p>
            </div>
          </div>
        </div>

        {/* Card FII's */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">FII's</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_fiis)}</p>
              <p className="text-xs text-gray-500 mt-1">Fundos Imobiliários</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos e Metas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna 1: Gráfico Bluechips */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Bluechips – Evolução e Metas</h2>
          
          {stats.bluechips.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhuma bluechip cadastrada</p>
          ) : (
            <div className="space-y-4">
              {stats.bluechips.map((item) => (
                <div key={item.codigo}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{item.codigo}</span>
                    <span className="text-xs text-gray-500">{item.percentual_meta.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(item.percentual_meta, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    {item.meta_tipo === 'quantidade' ? (
                      <>
                        <span className="text-xs text-gray-500">{item.quantidade_total} ações</span>
                        <span className="text-xs text-gray-500">Meta: {item.meta} ações</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-gray-500">{formatCurrency(item.valor_investido)}</span>
                        <span className="text-xs text-gray-500">Meta: {formatCurrency(item.meta)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coluna 2: Gráfico FII's */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">FII's – Evolução e Metas</h2>
          
          {stats.fiis.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhum FII cadastrado</p>
          ) : (
            <div className="space-y-4">
              {stats.fiis.map((item) => (
                <div key={item.codigo}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{item.codigo}</span>
                    <span className="text-xs text-gray-500">{item.percentual_meta.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(item.percentual_meta, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    {item.meta_tipo === 'quantidade' ? (
                      <>
                        <span className="text-xs text-gray-500">{item.quantidade_total} cotas</span>
                        <span className="text-xs text-gray-500">Meta: {item.meta} cotas</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-gray-500">{formatCurrency(item.valor_investido)}</span>
                        <span className="text-xs text-gray-500">Meta: {formatCurrency(item.meta)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coluna 3: Metas Renda Fixa */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Metas Renda Fixa</h2>
          
          <div className="space-y-6">
            {/* Poupança */}
            {stats.poupanca_metas.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Poupança</h3>
                <div className="space-y-3">
                  {stats.poupanca_metas.map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium text-gray-600">{item.banco}</span>
                        <span className="text-xs text-gray-500">{item.percentual.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(item.percentual, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-400">{formatCurrency(item.valor_atual)}</span>
                        <span className="text-xs text-gray-400">{formatCurrency(item.meta)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tesouro Direto */}
            {stats.tesouro_metas.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Tesouro Direto</h3>
                <div className="space-y-3">
                  {stats.tesouro_metas.map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs font-medium text-gray-600">{item.titulo}</span>
                        <span className="text-xs text-gray-500">{item.percentual.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-indigo-500 to-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(item.percentual, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-400">{formatCurrency(item.valor_atual)}</span>
                        <span className="text-xs text-gray-400">{formatCurrency(item.meta)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.poupanca_metas.length === 0 && stats.tesouro_metas.length === 0 && (
              <p className="text-gray-500 text-center py-8">Nenhuma meta cadastrada</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardNacional;
